// Unfinished business, tabling and the other motions (ADR 0065; the owner's
// decisions of 2026-10-25): what a meeting leaves unfinished and where it
// goes, the end-of-meeting review, postponing to a meeting or a date or
// indefinitely, referring to a committee, amending a pending motion and
// reconsidering a vote. The wording here is what Hub, the minutes, the
// packet and myBRE all show. Core decides; the staff UI only shows.

import type { MeetingBody, PersonRef } from './governance'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

// "October 14" from a meeting's local start (or a YYYY-MM-DD day).
export function meetingDayLabel(startsLocalOrDay: string): string {
  const month = Number(startsLocalOrDay.slice(5, 7))
  const day = Number(startsLocalOrDay.slice(8, 10))
  return `${MONTHS[month - 1] ?? ''} ${day}`
}

// "November 30, 2026".
export function longDateLabel(day: string): string {
  return `${meetingDayLabel(day)}, ${day.slice(0, 4)}`
}

// "the Finance Committee"; a committee named without the word gets it.
export function committeeLabel(name: string): string {
  return /\bcommittee\b/i.test(name) ? `the ${name}` : `the ${name} committee`
}

// ----------------------------------------------------------- carried items

export const CARRY_KINDS = ['tabled', 'postponed', 'carried', 'referred', 'returned'] as const
export type CarryKind = (typeof CARRY_KINDS)[number]

// A carried item's agenda item: the existing sources, auto_ref 'carry:<id>'.
export const CARRY_REF_PREFIX = 'carry:'
export const carryRef = (id: string) => `${CARRY_REF_PREFIX}${id}`
export const carryIdOfRef = (ref: string | null | undefined): string | null => (ref && ref.startsWith(CARRY_REF_PREFIX) ? ref.slice(CARRY_REF_PREFIX.length) : null)
export const carrySource = (kind: CarryKind) => (kind === 'tabled' ? ('tabled_motion' as const) : ('postponed_motion' as const))

// The label a carried item has on the agenda, in the packet, in myBRE and
// as the first line of its minutes.
export function carryLabel(c: { kind: CarryKind; sourceStartsLocal: string; originStartsLocal: string; sourceBody?: MeetingBody; sourceCommitteeName?: string | null }): string {
  switch (c.kind) {
    case 'tabled':
      return `Laid on the table at the ${meetingDayLabel(c.originStartsLocal)} meeting`
    case 'postponed':
      return `Postponed from the ${meetingDayLabel(c.sourceStartsLocal)} meeting`
    case 'carried':
      return `Carried from the ${meetingDayLabel(c.sourceStartsLocal)} meeting`
    case 'referred':
      return c.sourceBody === 'board' || !c.sourceBody ? `Referred by the board on ${meetingDayLabel(c.sourceStartsLocal)}` : `Referred by ${committeeLabel(c.sourceCommitteeName ?? 'committee')} on ${meetingDayLabel(c.sourceStartsLocal)}`
    case 'returned':
      return `Returned from ${committeeLabel(c.sourceCommitteeName ?? 'committee')}`
  }
}

// ----------------------------------------------------- the review at adjournment

export const REVIEW_KINDS = ['unreached', 'discussed', 'pending'] as const
export type ReviewKind = (typeof REVIEW_KINDS)[number]
export const REVIEW_CHOICES = ['carry', 'drop', 'refer', 'return'] as const
export type ReviewChoice = (typeof REVIEW_CHOICES)[number]
export const REVIEW_KIND_LABELS: Record<ReviewKind, string> = { unreached: 'Not taken up', discussed: 'Discussed, no motion', pending: 'Motion pending' }
export const REVIEW_CHOICE_LABELS: Record<ReviewChoice, string> = { carry: 'Carry to next meeting', drop: 'Drop', refer: 'Send to committee', return: 'Return to the board' }

// Owner decisions 2 and 3: not taken up, Carry; discussed without a
// motion, Drop; a motion still pending, Carry. A referral the committee
// took up goes back to the body that referred it.
export function defaultReviewChoice(kind: ReviewKind, referral: boolean): ReviewChoice {
  if (referral && kind !== 'unreached') return 'return'
  return kind === 'discussed' ? 'drop' : 'carry'
}

export type ReviewRowView = {
  key: string
  kind: ReviewKind
  agendaItemId: string | null
  motionId: string | null
  number: string | null
  title: string
  executive: boolean
  redacted: boolean
  // A referral on a committee's agenda: it may be returned.
  referral: boolean
  // Where "Return" sends it, in words.
  returnsTo: string | null
  defaultChoice: ReviewChoice
}

export type ReviewChoiceInput = { key: string; choice: ReviewChoice; committeeId?: string | null }

// One plain line in the minutes for each row of the review.
export function reviewMinutesLine(r: { kind: ReviewKind; choice: ReviewChoice; title: string; motion: boolean; committeeName?: string | null; returnsTo?: string | null }): string {
  const what = r.choice === 'carry' ? 'carried to the next meeting' : r.choice === 'drop' ? 'dropped' : r.choice === 'refer' ? `referred to ${committeeLabel(r.committeeName ?? 'committee')}` : `returned to ${r.returnsTo ?? 'the board'}`
  if (r.motion) return `Pending at adjournment: “${r.title}”; ${what}.`
  return `${r.kind === 'unreached' ? 'Not taken up' : 'Discussed without a motion'}; ${what}.`
}

// ----------------------------------------------- set aside: table, postpone, refer

export const SET_ASIDE_KINDS = ['table', 'postpone_next', 'postpone_meeting', 'postpone_date', 'postpone_indefinitely', 'refer'] as const
export type SetAsideKind = (typeof SET_ASIDE_KINDS)[number]
export const PROCEDURE_KINDS = [...SET_ASIDE_KINDS, 'amend', 'reconsider'] as const
export type ProcedureKind = (typeof PROCEDURE_KINDS)[number]

// The procedural motion's title and text, and what the main motion reads
// once it is set aside (its withdrawn reason, in the minutes).
export function setAsideWording(kind: SetAsideKind, input: { mainTitle: string; quoted: string; meetingStartsLocal?: string | null; date?: string | null; committeeName?: string | null }): { title: string; text: string; reason: string } {
  const t = (prefix: string) => `${prefix}: ${input.mainTitle}`.slice(0, 300)
  switch (kind) {
    case 'table':
      return { title: t('Lay on the table'), text: `Moved, that ${input.quoted} be laid on the table.`, reason: 'Laid on the table' }
    case 'postpone_next':
      return { title: t('Postpone to the next meeting'), text: `Moved, that ${input.quoted} be postponed to the next meeting.`, reason: 'Postponed to the next meeting' }
    case 'postpone_meeting': {
      const day = meetingDayLabel(input.meetingStartsLocal ?? '')
      return { title: t(`Postpone to the ${day} meeting`), text: `Moved, that ${input.quoted} be postponed to the ${day} meeting.`, reason: `Postponed to the ${day} meeting` }
    }
    case 'postpone_date': {
      const day = longDateLabel(input.date ?? '')
      return { title: t(`Postpone to ${day}`), text: `Moved, that ${input.quoted} be postponed to the first meeting on or after ${day}.`, reason: `Postponed to the first meeting on or after ${day}` }
    }
    case 'postpone_indefinitely':
      return { title: t('Postpone indefinitely'), text: `Moved, that ${input.quoted} be postponed indefinitely.`, reason: 'Postponed indefinitely' }
    case 'refer': {
      const to = committeeLabel(input.committeeName ?? 'committee')
      return { title: t(`Refer to ${to}`), text: `Moved, that ${input.quoted} be referred to ${to}.`, reason: `Referred to ${to}` }
    }
  }
}

export function amendmentWording(input: { mainTitle: string; quoted: string; title: string | null; text: string }): { title: string; text: string } {
  const retitled = input.title && input.title !== input.mainTitle ? `, titled “${input.title}”,` : ''
  return { title: `Amend: ${input.mainTitle}`.slice(0, 300), text: `Moved, that ${input.quoted} be amended${retitled} to read: “${input.text}”` }
}

// ADR 0073: an amendment of one section of a document motion.
export function sectionAmendmentWording(input: { mainTitle: string; quoted: string; name: string; title: string | null; text: string }): { title: string; text: string } {
  const retitled = input.title ? `, titled “${input.title}”,` : ''
  return { title: `Amend ${input.name}: ${input.mainTitle}`.slice(0, 300), text: `Moved, that ${input.quoted} be amended so that ${input.name}${retitled} reads: “${input.text}”` }
}

export function reconsiderWording(input: { mainTitle: string; quoted: string }): { title: string; text: string } {
  return { title: `Reconsider: ${input.mainTitle}`.slice(0, 300), text: `Moved, that the vote on ${input.quoted} be reconsidered.` }
}

// The motion a procedural motion acts on, as the conductor, myBRE and the
// minutes show it.
export type MotionProcedureView = {
  kind: ProcedureKind
  targetMotionId: string
  targetTitle: string
  toMeetingId: string | null
  toDate: string | null
  committeeName: string | null
  amendedTitle: string | null
  amendedText: string | null
  // A reconsideration: who may move it (voted on the prevailing side).
  eligibleMovers?: PersonRef[]
}

export const PROCEDURE_LABELS: Record<ProcedureKind, string> = {
  table: 'Lay on the table',
  postpone_next: 'Postpone',
  postpone_meeting: 'Postpone',
  postpone_date: 'Postpone',
  postpone_indefinitely: 'Postpone indefinitely',
  refer: 'Refer to committee',
  amend: 'Amendment',
  reconsider: 'Reconsider',
}

// ------------------------------------------------------------- open items

export type OpenItemView = {
  id: string
  kind: CarryKind
  label: string
  title: string
  executive: boolean
  redacted: boolean
  body: MeetingBody
  committeeId: string | null
  committeeName: string | null
  from: { meetingId: string; title: string; startsLocal: string }
  motionId: string | null
  // Where it goes next, in words, and the meeting it is on now if any.
  goesTo: string
  onAgendaOf: { meetingId: string; title: string; startsLocal: string; status: string } | null
  // A referral can go back to the body that referred it.
  canReturn: boolean
}
